import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import StaffNavbar from '../components/StaffNavbar';
import BackButton from '../components/BackButton';
import apiService from '../services/api';
import '../styles/StaffPages.css';

const SuperuserAddPoints = () => {
  const { userId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [currentUser, setCurrentUser] = useState(null);
  const [targetUser, setTargetUser] = useState(null);
  const [points, setPoints] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Check if user is superuser
        const user = await apiService.fetchCurrentUser();
        setCurrentUser(user);

        if (!user?.is_superuser) {
          navigate('/staff/dashboard/');
          return;
        }

        // Get target user from location state or fetch from API
        if (location.state?.user) {
          setTargetUser(location.state.user);
        } else {
          // If no user in state, we'd need to fetch user details
          // For now, redirect back to user management
          navigate('/superuser/users/');
          return;
        }

      } catch (error) {
        console.error('Failed to fetch user data:', error);
        navigate('/login/');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate, userId, location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!points || isNaN(points) || parseInt(points) <= 0) {
      setError('Please enter a valid positive number of points');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await apiService.addPointsToUser(userId, parseInt(points));
      navigate('/superuser/users/', {
        state: {
          message: `Successfully added ${points} points!`,
          type: 'success'
        }
      });
    } catch (error) {
      console.error('Failed to add points:', error);
      const errorMessage = error.response?.data?.error || 'Failed to add points. Please try again.';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <StaffNavbar activeSection="superuser" />
        <div className="admin-main">
          <div className="admin-content">
            <div style={{
              textAlign: 'center',
              padding: '3rem',
              color: '#666'
            }}>
              Loading...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <StaffNavbar activeSection="superuser" />
      <div className="admin-main">
        <div className="admin-content">
          <BackButton text="Back to User Management" to="/superuser/users/" />

          <h1 className="staff-page-header">Add Points</h1>
          <p className="staff-page-subheader">
            Grant points to {targetUser?.name} ({targetUser?.email})
          </p>

          <div className="add-reward-section">
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">
                  Points to Add
                </label>
                <input
                  type="number"
                  value={points}
                  onChange={(e) => setPoints(e.target.value)}
                  placeholder="Enter number of points"
                  min="1"
                  className="form-input points-input"
                  style={{
                    padding: '20px',
                    fontSize: '18px',
                    minHeight: '60px',
                    width: '100%',
                    maxWidth: '600px'
                  }}
                  required
                />
              </div>

              {error && (
                <div style={{
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  padding: '12px',
                  borderRadius: '12px',
                  marginBottom: '16px'
                }}>
                  <p style={{
                    margin: '0',
                    fontSize: '14px',
                    color: '#dc2626',
                    fontFamily: '"Baikal Book"'
                  }}>
                    {error}
                  </p>
                </div>
              )}

              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => navigate('/superuser/users/')}
                  style={{
                    padding: '8px 12px',
                    border: '0.5px solid #E0E0E0',
                    borderRadius: '100px',
                    backgroundColor: 'white',
                    color: '#000',
                    fontSize: '14px',
                    fontFamily: '"Baikal Book"',
                    fontWeight: '340',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="staff-page-button"
                  style={{
                    backgroundColor: submitting ? '#9ca3af' : '#00C8FF',
                    cursor: submitting ? 'not-allowed' : 'pointer'
                  }}
                >
                  <span className="staff-page-button-text">
                    {submitting ? 'Adding Points...' : 'Add Points'}
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Toast notification */}
      {toast && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: toast.type === 'error' ? '#EF4444' : '#10B981',
          color: 'white',
          padding: '12px 20px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          zIndex: 1001,
          fontSize: '0.875rem',
          fontWeight: '500',
          maxWidth: '400px'
        }}>
          {toast.message}
          <button
            onClick={() => setToast(null)}
            style={{
              marginLeft: '12px',
              backgroundColor: 'transparent',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              fontSize: '1rem',
              padding: '0',
              lineHeight: '1'
            }}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

export default SuperuserAddPoints;